/**
 * Copyright (C) 2019 User X, LLC http://whoisuserx.com <info@userx.co>
 *
 * This program is the intellectual property of User X, LLC. You may
 * not redistribute and/or modify it without written consent by controlling
 * entities of User X, LLC.
 *
 * You should have received a copy of our Web Developer Agreement which
 * outlines proper use and distribution of this program. If you did not
 * please email info@userx.co to request your copy.
 */
/**
 * File created by John M. Woodcock < john@userx.co >
 * on 8/30/2023
 * for userx-support
 */

import React, {useEffect, useRef, useState} from 'react';
import CompanyForm from "./CompanyForm";
import ContactsForm from "./ContactsForm";
import {addCompany, urlExists} from "../../../_utilities/companyUtilities";
import CompanyPreferences from "../CompanyPreferences";
import supportAPI from "../../../_apis/supportAPI";
import useLocalStorage from "../../../_hooks/useLocalStorage";
import {gaEvent} from "../../../system/ga";


const RegistrationForm = () => {
          const [errorMsg, setErrorMsg] = useState('');
          const [step, setStep] = useState(0);
          const [companyInfo, setCompanyInfo] = useState({name: '', url: '', description: ''});
          const [contacts, setContacts] = useState([]);
          const [prefsSaved, setPrefsSaved] = useState(false);
          const [pendingPrefs, setPendingPrefs] = useLocalStorage('uxCompanyPrefs', {readiness: 'quarterly', capabilities: ['maintenance']});
          const [pendingReview, setPendingReview] = useState(null);
          const [prefs, setPrefs] = useState({readiness: 'quarterly', capabilities: ['maintenance'], estimatedCosts: 20});
          const firedEventsRef = useRef(new Set());

          useEffect(() => {
              try {
                  const params = new URLSearchParams(window.location.search || '');
                  const readinessParam = params.get('readiness');
                  const capabilitiesParam = params.get('capabilities');
                  const reviewIdParam = params.get('review_id');

                  // Check for readiness and capabilities parameters
                  if (readinessParam || capabilitiesParam) {
                      const capabilities = capabilitiesParam
                          ? decodeURIComponent(capabilitiesParam).split(',').map(s => s.trim()).filter(Boolean)
                          : [];

                      const prefs = {
                          readiness:    readinessParam || 'quarterly',
                          capabilities: capabilities.length ? capabilities : ['maintenance']
                      };

                      // persist for CompanyPreferences (and other consumers)
                      setPendingPrefs(prefs);
                  }

                  // Check for review_id parameter
                  if (reviewIdParam) {
                      console.log('Found review_id parameter:', reviewIdParam);
                      const numericReviewId = parseInt(reviewIdParam, 10);
                      if (Number.isNaN(numericReviewId) || numericReviewId <= 0) {
                          console.error('Invalid review_id parameter: must be a positive integer, got', reviewIdParam);
                      } else {
                          supportAPI.get(`/review/${numericReviewId}/tickets`).then(response => {
                              const review = response.data;
                              console.log(review);
                              if (review) {
                                  // Pre-fill company info
                                  setCompanyInfo(prev => ({
                                      ...prev,
                                      name:          review.prospect_company || prev.name,
                                      url:           review.prospect_url || prev.url,
                                      status:        2,
                                      services:      2,
                                      default_agent: 1
                                  }));

                                  // Store review info for post-registration (as string for sessionStorage)
                                  setPendingReview(review);

                                  // Track link click
                                  supportAPI.get(`/review/${numericReviewId}/link`);
                              }
                          }).catch(err => {
                              console.error('Failed to load review:', err);
                          });
                      }
                  }
              } catch (err) {
                  // ignore parsing errors
                  console.error('Registration param parse error', err);
              }

              handleNextStep();
          }, []);

          useEffect(() => {
              switch (step) {
                  case 2:
                      if (!firedEventsRef.current.has('reg_step1_company')) {
                          sendGaEvent('reg_step1_company', {
                              action:   'reg_step1_company', // company_info_completed, contacts_added, preferences_set, registration_completed
                              category: 'Registration',
                              label:    'company_id', // e.g. company_id or ticket_id for context
                              value:    companyInfo.company_id,
                          });
                          firedEventsRef.current.add('reg_step1_company');
                      }
                      break;
                  case 3:
                      if (!firedEventsRef.current.has('reg_step2_contacts')) {
                          sendGaEvent('reg_step2_contacts', {
                              action:   'reg_step2_contacts', // company_info_completed, contacts_added, preferences_set, registration_completed
                              category: 'Registration',
                              label:    'company_id', // e.g. company_id or ticket_id for context
                              value:    companyInfo.company_id,
                          });
                          firedEventsRef.current.add('reg_step2_contacts');
                      }
                      break;
                  case 4:
                      if (!firedEventsRef.current.has('reg_step3_prefs')) {
                          // get estimated costs based on preferences for GA value (requires company_id from step 1, so we wait until step 4 to ensure we have it)
                          sendGaEvent('reg_step3_prefs', {
                              action:   'reg_step3_prefs', // company_info_completed, contacts_added, preferences_set, registration_completed
                              category: 'Registration',
                              company_id:    companyInfo.company_id, // e.g. company_id or ticket_id for context
                              maintenance_frequency: prefs.maintenanceFrequency,
                              currency: 'USD',
                              value:    parseFloat(prefs.monthlyEstimate),
                          });
                          firedEventsRef.current.add('reg_step3_prefs');
                      }
                      sendGaEvent('reg_step4_complete', companyInfo);

                      // Check for pending review conversion when moving to step 4 (completion)
                      if (pendingReview && pendingReview.tickets.length > 0) {
                          const pendingReviewId = parseInt(pendingReview.review_id);
                          const primaryContact = contacts.find(c => c.primary === 1 || c.primary === '1') || contacts[0];
                          try {
                              if (!pendingReviewId) throw new Error('Invalid pending review ID for conversion.');
                              if (!companyInfo.company_id) throw new Error('Company ID missing for review conversion.');
                              if (!primaryContact || primaryContact === undefined) throw new Error('No contacts found for review conversion.');

                              const convertPayload = {
                                  company_id: companyInfo.company_id,
                                  contact_id: primaryContact.contact_id
                              };
                              console.log('Converting pending review', pendingReviewId, 'to company', companyInfo.company_id, convertPayload);
                              supportAPI.post(`/review/${pendingReviewId}/convert`, convertPayload).then(response => {
                                  sessionStorage.removeItem('pending_review_id');
                                  console.log('Review tickets created successfully');
                              });
                          } catch (err) {
                              console.error('Failed to create review tickets:', {
                                  error:     err.message,
                                  response:  err.response?.data,
                                  status:    err.response?.status,
                                  reviewId:  pendingReviewId,
                                  companyId: companyInfo.company_id,
                                  contactId: primaryContact?.contact_id
                              });
                          }
                      }
                      break;
                  default:
                  // Do nothing for step 0 or unrecognized steps
              }
          }, [step]);

          const handleCompanyInfoChange = (e) => {
              const {name, value} = e.target;
              setCompanyInfo((prevInfo) => ({...prevInfo, [name]: value}));
          };

          const handleCompanySave = async () => {
              try {
                  if (!validateCompany()) throw new Error('Please fill out all fields completely.');

                  const exists = await urlExists(companyInfo.url);
                  if (exists) throw new Error('A company with this URL already exists.');

                  const response = await addCompany(companyInfo);
                  setCompanyInfo(prev => ({...prev, company_id: response.company_id}));
                  handleNextStep();
              } catch (err) {
                  setErrorMsg(err.message || 'Unexpected error occurred.');
              }
          };

          const handlePrefsSaved = (savedPrefs) => {
              setPrefs(savedPrefs);
              setPrefsSaved(true);
          }

          // Handle progression to next step
          // Note: This function is async to support review conversion when moving to step 4
          const handleNextStep = () => {
              const nextStep = step + 1;
              setStep(() => nextStep);
          };

          const handlePrevStep = () => {
              const nextStep = step - 1;
              setStep(() => nextStep);
          }

          const validateCompany = () => {
              if (companyInfo.name !== '' && companyInfo.url !== '') {
                  return true;
              }
              return false;
          }

          const validateContacts = () => {
              if (contacts.length >= 1) {
                  return true;
              }
              return false;
          }

          const save = async () => {
              try {
                  const newCompany = await addCompany(companyInfo);
              } catch (err) {
                  console.error(err);
                  switch (err.response.status) {
                      case 422:
                          setErrorMsg('A company with this URL already exists.');
                          break;
                      default:
                          setErrorMsg(err.message);
                  }
              }
          };

          const sendGaEvent = (action, payload) => {
              // send GA event (gaEvent accepts an object as used elsewhere in this file)
              try {
                  gaEvent(payload);
              } catch (e) {
                  if (process.env.NODE_ENV !== 'production') console.info('GA event failed', e, payload);
              }
          }

          const renderStep = () => {
              switch (step) {
                  case 1:
                      return (<>
                          <p className={`text-center`}>Please fill out the form below to register.</p>
                          <CompanyForm companyInfo={companyInfo} onChange={handleCompanyInfoChange}/>
                          <p className={`mt-3 text-center`}>Your data is secure and used only for support setup and maintenance.</p>
                          <button
                              disabled={!validateCompany()}
                              className={`btn btn-primary d-block mx-auto`}
                              type="button"
                              onClick={handleCompanySave}>
                              Add Contacts
                          </button>
                      </>);
                  case 2:
                      return (<>
                          <p className={`text-center`}>Company details saved! Now, let’s identify who should have access to your support portal.</p>
                          <ContactsForm company_id={companyInfo.company_id}
                                        onContactsChanged={list => setContacts(prev => list)}/>
                          <p className={`mt-3 text-center`}>You can always add or remove contacts later from your dashboard.</p>
                          <div className="d-flex justify-content-center align-items-center">
                              <button
                                  className={`d-block btn btn-primary d-block mx-2`}
                                  type="button"
                                  onClick={handlePrevStep}>
                                  Back to Company Info
                              </button>
                              <button
                                  disabled={!validateContacts()}
                                  className={`btn btn-primary d-block mx-2`}
                                  type="button"
                                  onClick={handleNextStep}>
                                  Set Preferences
                              </button>
                          </div>
                      </>);
                  case 3:
                      return (
                          <>
                              <CompanyPreferences company_id={companyInfo.company_id}
                                                  pendingPrefs={pendingPrefs}
                                                  onSaved={handlePrefsSaved} />
                              <p className={`mt-3 text-center`}>Be sure to save your preferences before you continue.</p>
                              <div className="d-flex justify-content-center align-items-center">
                                  <button
                                      className={`d-block btn btn-primary d-block mx-2`}
                                      type="button"
                                      onClick={handlePrevStep}>
                                      Back to Contacts
                                  </button>
                                  <button
                                      disabled={!prefsSaved}
                                      className={`btn btn-primary d-block mx-2`}
                                      type="button"
                                      onClick={handleNextStep}>
                                      Complete Registration
                                  </button>
                              </div>
                          </>
                      )
                          ;
                  case 4:
                      console.log('Contacts at completion:', contacts);
                      return (
                          <div className={`mx-auto`} style={{width: '90%', maxWidth: '700px'}}>
                              <div className={'d-flex justify-content-center align-items-center'}>
                                  <div className="" style={{fontSize: '64px'}}>🎉</div>
                                  <div className={'pt-3'}>
                                      <h2 className="fw-bold mb-0">Welcome to User X Support!</h2>
                                      <p className="fs-5 ps-1">We're thrilled to have <strong>{companyInfo.name}</strong> on
                                          board.</p>
                                  </div>
                              </div>
                              <div className={'d-flex gap-5'}>
                                  <div className={'w-50'}>
                                      <p>From website tweaks to major updates, from security concerns to performance
                                          optimization —
                                          our support platform is here to keep your business moving forward. Whether you
                                          need a quick fix
                                          or long-term strategy, just open a ticket and we’ll be right there with
                                          you.</p>
                                      <p>To get the ball rolling we will have a <strong>kickoff meeting</strong> to
                                          securely exchange FTP/WordPress access. This meeting ensures we can start
                                          supporting your site right away.</p>

                                  </div>
                                  <div>
                                      <h4>What’s next?</h4>
                                      <ul className="list-unstyled mt-2 small">
                                          <li className={'mb-2'}><i className={`bi bi-check-square-fill text-success`}></i> Check your inbox to complete account setup</li>
                                          <li className={'mb-2'}><i className={`bi bi-check-square-fill text-success`}></i> Log in to view your support dashboard</li>
                                          <li className={'mb-2'}><i className={`bi bi-check-square-fill text-success`}></i> Submit ticket for <strong>kickoff meeting</strong></li>
                                          <li className={'mb-2'}><i className={`bi bi-check-square-fill text-success`}></i> Gather FTP & WordPress access for meeting</li>
                                          <li className={'mb-2'}><i className={`bi bi-square text-success`}></i> <strong><a
                                              href='https://www.whoisuserx.com/blog/contact-us/' target='_blank'>Schedule a
                                              kickoff meeting</a></strong></li>
                                      </ul>
                                      <p>We are looking forward to working with you and your company!</p>
                                  </div>

                              </div>

                          </div>
                      );
                  default:
                      return null;
              }
          };

          return (
              <div className={`py-2 px-3 m-3 mx-auto`} style={{width: '90%', maxWidth: '1180px'}}>
                  <h2 className={`text-center`}>Register for User X Support</h2>
                  <h4 className={`text-center`} style={{color: '#999'}}>
                      <span className={step === 1 ? `text-dark` : ''}>Company Info</span>
                      &nbsp;&nbsp;<i className={`bi bi-arrow-right`}></i>&nbsp;&nbsp;
                      <span className={step === 2 ? `text-dark` : ''}>Contacts</span>
                      &nbsp;&nbsp;<i className={`bi bi-arrow-right`}></i>&nbsp;&nbsp;
                      <span className={step === 3 ? `text-dark` : ''}>Preferences</span>
                      &nbsp;&nbsp;<i className={`bi bi-arrow-right`}></i>&nbsp;&nbsp;
                      <span className={step === 4 ? `text-dark` : ''}>Welcome</span>
                  </h4>
                  <hr/>
                  {errorMsg !== '' ? <div className={`alert alert-danger`} role={`alert`}>{errorMsg}</div> : null}
                  {renderStep()}
              </div>
          );
      }
;

export default RegistrationForm;
